import { useEffect, useRef } from 'react';

/**
 * A REAL QR code. SOW §2.2 asks for "Member Card with QR".
 *
 * What was here before was a drawing: a PDF417-shaped picture generated from the
 * token's hash, honest about it in its own comment, and unreadable by any scanner.
 * It made the card look like a card and left the cashier typing the member number
 * by hand.
 *
 * WHY THIS IS HAND-WRITTEN rather than a dependency. The card token is bearer
 * material — scanning it authorises a spend — and every QR library on npm is a
 * package that sees that value, in a bundle that ships to every customer. About
 * 250 lines of well-understood arithmetic is a better trade than a supply-chain
 * surface over the one string in the product that spends money. There is nothing
 * novel here; the spec is ISO/IEC 18004 and this implements the part of it we
 * need.
 *
 * SCOPE, deliberately narrow:
 *   - Byte mode only. The token is base64url, which is not alphanumeric-mode
 *     safe (that mode's alphabet is uppercase and nine symbols).
 *   - Error correction level M — about 15% recoverable. L is tempting for a
 *     smaller symbol, but this is scanned off a phone screen with fingerprints
 *     on it, at an angle, under a counter light.
 *   - Versions 1–25, which covers 997 bytes at level M. A card token is 43;
 *     a signed offline pass (TC-03 / TC-04) is around 550, which is why this
 *     went past version 10 - and past version 6, which is where the spec starts
 *     requiring the version-information blocks this used to leave out.
 *
 * If a payload ever exceeds that, this throws rather than rendering a symbol that
 * scans as the wrong thing.
 */
export function QrCode({
  value,
  size = 220,
  label,
}: {
  value: string;
  size?: number;
  label?: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (canvas === null) return;

    let modules: boolean[][];

    try {
      modules = encode(value);
    } catch {
      // A payload we cannot encode must not render a plausible-looking symbol.
      // Blank canvas plus the member number underneath is a worse card and an
      // honest one.
      return;
    }

    const count = modules.length;

    // A QUIET ZONE IS PART OF THE SYMBOL, not padding. Four modules, per the
    // spec: without it a scanner cannot find the symbol's edge against a
    // background, and the code reads as "no QR here" rather than as a bad one.
    const quiet = 4;
    const total = count + quiet * 2;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;

    const ctx = canvas.getContext('2d');
    if (ctx === null) return;

    ctx.scale(dpr, dpr);

    // WHITE, not the surface colour, and black, not the text colour. A scanner
    // reads contrast; a themed QR on a dark background is a QR that stops
    // working at night. These two are not brand colours and must not become
    // tokens.
    ctx.fillStyle = '#FFFFFF'; // brand-allow-hex: a QR's quiet zone must be white in either theme
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = '#000000'; // brand-allow-hex: a QR's modules must be black in either theme

    // Rounded to whole device pixels. A fractional module size antialiases every
    // edge into grey, and a scanner reading a grid of grey squares fails on
    // exactly the phones with the least forgiving cameras.
    const scale = Math.max(1, Math.floor((size * dpr) / total)) / dpr;
    const offset = (size - scale * total) / 2 + quiet * scale;

    for (let y = 0; y < count; y++) {
      for (let x = 0; x < count; x++) {
        if (modules[y]?.[x] === true) {
          ctx.fillRect(offset + x * scale, offset + y * scale, scale, scale);
        }
      }
    }
  }, [value, size]);

  return (
    <canvas
      ref={ref}
      className="qr"
      style={{ width: size, height: size }}
      // The token must never be read aloud in a shop, and the member number sits
      // in real text below. A label describes what the graphic IS.
      role="img"
      aria-label={label ?? 'Your membership QR code'}
    />
  );
}

/* ========================================================================== */
/*  ISO/IEC 18004, the parts we need. Byte mode, level M, versions 1-10.      */
/* ========================================================================== */

/*
 * TYPED ARRAYS THROUGHOUT, and not for speed.
 *
 * This project compiles with `noUncheckedIndexedAccess`, which types `array[i]`
 * as `T | undefined` — correct, and exactly right for the rest of the codebase.
 * Bit arithmetic indexes constantly, and the honest options were a hundred
 * non-null assertions or containers whose index type is already `number`.
 * Assertions would have buried the two or three places where a bound genuinely
 * needs thinking about; typed arrays remove the question instead of silencing it.
 */

/**
 * A numeric read from one of the tables or grids below.
 *
 * `noUncheckedIndexedAccess` is on for this project — rightly, and it catches
 * real bugs elsewhere — and it types every `array[i]` as `number | undefined`,
 * typed arrays included. Bit arithmetic indexes on almost every line, so the
 * choice was one asserted accessor or forty non-null assertions scattered
 * through the maths. Forty `!`s would hide the two or three places where a bound
 * genuinely needs thought; this puts the assertion in exactly one place.
 *
 * Every call site below indexes within a length the same function just
 * allocated or a table indexed by a version already range-checked. The one place
 * a read CAN go past the end — the remainder bits at the tail of the symbol —
 * checks the length itself and does not come through here.
 */
const at = (array: Uint8Array | Int8Array | Uint16Array | readonly number[], i: number): number =>
  array[i] as number;

/**
 * Data codewords available at level M, indexed by version (1-based). ISO/IEC
 * 18004 table 9, transcribed; the block structure below reproduces each total.
 */
const DATA_CODEWORDS_M = new Uint16Array([
  0, 16, 28, 44, 64, 86, 108, 124, 154, 182, 216,
  254, 290, 334, 365, 415, 453, 507, 563, 627, 669,
  714, 782, 860, 914, 1000,
]);

/** EC codewords per block at level M, by version. */
const EC_PER_BLOCK_M = new Uint8Array([
  0, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26,
  30, 22, 22, 24, 24, 28, 28, 26, 26, 26,
  26, 28, 28, 28, 28,
]);

/** Number of EC blocks at level M, by version. */
const BLOCKS_M = new Uint8Array([
  0, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5,
  5, 8, 9, 9, 10, 10, 11, 13, 14, 16,
  17, 17, 18, 20, 21,
]);

const MIN_VERSION = 1;
const MAX_VERSION = 25;

/**
 * Exported for the test harness only. `scripts/qr-verify.mjs` compares this
 * matrix, module for module, against the reference `qrcode` implementation and
 * then decodes the render with `jsqr` — because a QR encoder that is subtly
 * wrong produces a symbol that looks perfect and scans as nothing.
 */
export function encode(text: string): boolean[][] {
  const data = new TextEncoder().encode(text);

  // Byte mode: 4 bits of mode, then the length, then the bytes.
  let version = MIN_VERSION;
  while (version <= MAX_VERSION) {
    const lengthBits = version < 10 ? 8 : 16;
    if (4 + lengthBits + data.length * 8 <= at(DATA_CODEWORDS_M, version) * 8) break;
    version++;
  }

  if (version > MAX_VERSION) {
    throw new Error('Payload too large for the supported QR versions.');
  }

  const lengthBits = version < 10 ? 8 : 16;
  const totalCodewords = at(DATA_CODEWORDS_M, version);

  const codewords = new Uint8Array(totalCodewords);
  let bitCount = 0;

  const push = (value: number, count: number) => {
    for (let i = count - 1; i >= 0; i--) {
      const bit = (value >> i) & 1;
      if (bit === 1) codewords[bitCount >> 3] = at(codewords, bitCount >> 3) | (0x80 >> (bitCount & 7));
      bitCount++;
    }
  };

  push(0b0100, 4); // byte mode
  push(data.length, lengthBits);
  for (const byte of data) push(byte, 8);

  // Terminator, up to four bits. Everything past it is already zero because the
  // buffer started that way, so there is nothing to pad to the byte boundary.
  push(0, Math.min(4, totalCodewords * 8 - bitCount));

  // The spec's alternating pad bytes fill whatever is left.
  const PAD = [0xec, 0x11] as const;
  const usedBytes = Math.ceil(bitCount / 8);
  for (let i = usedBytes; i < totalCodewords; i++) {
    codewords[i] = at(PAD, (i - usedBytes) % 2);
  }

  return render(addErrorCorrection(codewords, version), version);
}

/* ------------------------------------------------- Reed-Solomon over GF(256) */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);

(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    // The QR field's primitive polynomial, x^8 + x^4 + x^3 + x^2 + 1.
    x = (x << 1) ^ (x & 0x80 ? 0x11d : 0);
  }
  for (let i = 255; i < 512; i++) EXP[i] = at(EXP, i - 255);
})();

const mul = (a: number, b: number): number =>
  a === 0 || b === 0 ? 0 : at(EXP, at(LOG, a) + at(LOG, b));

function generatorPoly(degree: number): Uint8Array {
  let poly = new Uint8Array([1]);

  for (let i = 0; i < degree; i++) {
    const next = new Uint8Array(poly.length + 1);

    for (let j = 0; j < poly.length; j++) {
      next[j] = at(next, j) ^ at(poly, j);
      next[j + 1] = at(next, j + 1) ^ mul(at(poly, j), at(EXP, i));
    }

    poly = next;
  }

  return poly;
}

function remainder(block: Uint8Array, ecCount: number): Uint8Array {
  const gen = generatorPoly(ecCount);
  const out = new Uint8Array(ecCount);

  for (const byte of block) {
    const factor = byte ^ at(out, 0);

    // Shift left by one, dropping the term just consumed.
    out.copyWithin(0, 1);
    out[ecCount - 1] = 0;

    for (let i = 0; i < ecCount; i++) {
      out[i] = at(out, i) ^ mul(at(gen, i + 1), factor);
    }
  }

  return out;
}

/**
 * Split into blocks, compute EC for each, then INTERLEAVE.
 *
 * The interleaving is what makes error correction work in practice: a coffee ring
 * on the card damages one contiguous area of the symbol, and interleaving spreads
 * that damage across every block instead of destroying one of them entirely.
 */
function addErrorCorrection(codewords: Uint8Array, version: number): Uint8Array {
  const blockCount = at(BLOCKS_M, version);
  const ecPerBlock = at(EC_PER_BLOCK_M, version);

  const shortLength = Math.floor(codewords.length / blockCount);
  const longBlocks = codewords.length % blockCount;

  const blocks: Uint8Array[] = [];
  const ecBlocks: Uint8Array[] = [];
  let pos = 0;

  for (let i = 0; i < blockCount; i++) {
    const length = shortLength + (i >= blockCount - longBlocks ? 1 : 0);
    const block = codewords.subarray(pos, pos + length);
    pos += length;

    blocks.push(block);
    ecBlocks.push(remainder(block, ecPerBlock));
  }

  const out = new Uint8Array(codewords.length + ecPerBlock * blockCount);
  let cursor = 0;

  const maxData = shortLength + (longBlocks > 0 ? 1 : 0);

  for (let i = 0; i < maxData; i++) {
    for (const block of blocks) {
      if (i < block.length) out[cursor++] = at(block, i);
    }
  }

  for (let i = 0; i < ecPerBlock; i++) {
    for (const block of ecBlocks) out[cursor++] = at(block, i);
  }

  return out;
}

/* ------------------------------------------------------------- the symbol -- */

/**
 * -1 is "not yet placed", which is how the data walk knows what to skip. A plain
 * 0/1 grid would let data overwrite the finder patterns, and the result looks
 * almost right.
 */
const UNSET = -1;

function render(codewords: Uint8Array, version: number): boolean[][] {
  const size = version * 4 + 17;
  const modules = new Int8Array(size * size).fill(UNSET);

  placeFinder(modules, size, 0, 0);
  placeFinder(modules, size, size - 7, 0);
  placeFinder(modules, size, 0, size - 7);
  placeAlignment(modules, size, version);
  placeTiming(modules, size);

  // The dark module. Fixed position, always set — scanners rely on it.
  modules[(size - 8) * size + 8] = 1;

  reserveFormat(modules, size);
  placeVersion(modules, size, version);

  // A RECORDED function-module mask, not a geometric one.
  //
  // Everything placed so far is a function module; everything still UNSET is
  // data. Snapshotting that is exact for every version, whereas re-deriving the
  // geometry later means knowing each version's alignment table a second time —
  // and the copy that only the masking code consults is the copy nobody notices
  // is wrong.
  const isFunction = new Uint8Array(size * size);
  for (let i = 0; i < modules.length; i++) {
    isFunction[i] = at(modules, i) === UNSET ? 0 : 1;
  }

  placeData(modules, size, codewords);

  // Mask 0 — the checkerboard — chosen without evaluating the eight candidates.
  // The spec picks by penalty score; skipping that costs a little scan
  // reliability on adversarial payloads and saves a good deal of code. Our
  // payload is a random base64url token, which produces none of the large
  // uniform areas the other masks exist to break up.
  applyMask(modules, isFunction, size);
  placeFormat(modules, size);

  return Array.from({ length: size }, (_unused, y) =>
    Array.from({ length: size }, (_alsoUnused, x) => at(modules, y * size + x) === 1));
}

function placeFinder(modules: Int8Array, size: number, x: number, y: number) {
  for (let dy = -1; dy <= 7; dy++) {
    for (let dx = -1; dx <= 7; dx++) {
      const py = y + dy;
      const px = x + dx;

      if (py < 0 || py >= size || px < 0 || px >= size) continue;

      // THE SEPARATOR IS PART OF THE PATTERN. dx or dy of -1 or 7 is the light
      // ring around the finder and must be explicitly light, not merely unset.
      // Falling through to the `outer` test below set the whole top edge of that
      // ring dark, fusing the finder into the timing row — after which a scanner
      // cannot locate the symbol at all.
      if (dx === -1 || dx === 7 || dy === -1 || dy === 7) {
        modules[py * size + px] = 0;
        continue;
      }

      const outer = dx === 0 || dx === 6 || dy === 0 || dy === 6;
      const inner = dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4;

      modules[py * size + px] = outer || inner ? 1 : 0;
    }
  }
}

/**
 * The spec's alignment-centre table, versions 1-25.
 *
 * A COMPUTED TABLE WOULD BE WRONG. The positions are not evenly spaced — the
 * spec's own values are irregular — so this is transcribed, not derived. A switch
 * rather than an array of arrays because the latter needs a bounds assertion at
 * every read for nothing gained.
 */
function alignmentCentres(version: number): readonly number[] {
  switch (version) {
    case 2: return [6, 18];
    case 3: return [6, 22];
    case 4: return [6, 26];
    case 5: return [6, 30];
    case 6: return [6, 34];
    case 7: return [6, 22, 38];
    case 8: return [6, 24, 42];
    case 9: return [6, 26, 46];
    case 10: return [6, 28, 50];
    case 11: return [6, 30, 54];
    case 12: return [6, 32, 58];
    case 13: return [6, 34, 62];
    case 14: return [6, 26, 46, 66];
    case 15: return [6, 26, 48, 70];
    case 16: return [6, 26, 50, 74];
    case 17: return [6, 30, 54, 78];
    case 18: return [6, 30, 56, 82];
    case 19: return [6, 30, 58, 86];
    case 20: return [6, 34, 62, 90];
    case 21: return [6, 28, 50, 72, 94];
    case 22: return [6, 26, 50, 74, 98];
    case 23: return [6, 30, 54, 78, 102];
    case 24: return [6, 28, 54, 80, 106];
    case 25: return [6, 32, 58, 84, 110];
    default: return [];
  }
}

function placeAlignment(modules: Int8Array, size: number, version: number) {
  const centres = alignmentCentres(version);
  if (centres.length === 0) return;

  const last = at(centres, centres.length - 1);

  for (const y of centres) {
    for (const x of centres) {
      // The three that would collide with a finder pattern.
      const nearFinder =
        (x === 6 && y === 6)
        || (x === 6 && y === last)
        || (x === last && y === 6);

      if (nearFinder) continue;

      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          modules[(y + dy) * size + (x + dx)] =
            Math.max(Math.abs(dx), Math.abs(dy)) === 1 ? 0 : 1;
        }
      }
    }
  }
}

function placeTiming(modules: Int8Array, size: number) {
  for (let i = 8; i < size - 8; i++) {
    const on = i % 2 === 0 ? 1 : 0;
    modules[6 * size + i] = on;
    modules[i * size + 6] = on;
  }
}

/**
 * Version information, versions 7 and up: the version number, BCH(18,6)-encoded,
 * in two 3x6 blocks - above the bottom-left finder and left of the top-right one.
 *
 * Versions 7-10 were "supported" without it. Nothing had ever rendered past
 * version 3, so it never showed; a scanner reading a version-7 symbol without
 * these blocks has to guess the version from the module count, and many do not.
 */
function placeVersion(modules: Int8Array, size: number, version: number) {
  if (version < 7) return;

  let rem = version;
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  const bits = (version << 12) | rem;

  for (let i = 0; i < 18; i++) {
    const on = ((bits >> i) & 1) === 1 ? 1 : 0;
    const a = size - 11 + (i % 3);
    const b = Math.floor(i / 3);

    modules[b * size + a] = on; // top-right block: row b, column a
    modules[a * size + b] = on; // bottom-left block: row a, column b
  }
}

function reserveFormat(modules: Int8Array, size: number) {
  for (let i = 0; i < 9; i++) {
    if (at(modules, 8 * size + i) === UNSET) modules[8 * size + i] = 0;
    if (at(modules, i * size + 8) === UNSET) modules[i * size + 8] = 0;
  }

  for (let i = 0; i < 8; i++) {
    if (at(modules, 8 * size + (size - 1 - i)) === UNSET) modules[8 * size + (size - 1 - i)] = 0;
    if (at(modules, (size - 1 - i) * size + 8) === UNSET) modules[(size - 1 - i) * size + 8] = 0;
  }
}

/**
 * The data walk: two columns at a time, right to left, alternating up and down,
 * skipping column 6 (the vertical timing pattern) and anything already placed.
 */
function placeData(modules: Int8Array, size: number, codewords: Uint8Array) {
  let bit = 0;
  let upward = true;

  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;

    for (let vert = 0; vert < size; vert++) {
      for (let col = 0; col < 2; col++) {
        const x = right - col;
        const y = upward ? size - 1 - vert : vert;
        const cell = y * size + x;

        if (at(modules, cell) !== UNSET) continue;

        // Past the end of the data is a light module. The remainder bits at the
        // tail of a symbol are exactly that, and reading `undefined` there would
        // be an error rather than the zero the spec asks for.
        const byteIndex = bit >> 3;
        const value = byteIndex >= codewords.length
          ? 0
          : (at(codewords, byteIndex) >> (7 - (bit & 7))) & 1;

        modules[cell] = value;
        bit++;
      }
    }

    upward = !upward;
  }
}

/** Mask 0: (row + column) mod 2 === 0. Applied to data modules only. */
function applyMask(modules: Int8Array, isFunction: Uint8Array, size: number) {
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const cell = y * size + x;

      if (at(isFunction, cell) === 1) continue;
      if ((x + y) % 2 === 0) modules[cell] = at(modules, cell) === 1 ? 0 : 1;
    }
  }
}

/**
 * Format information: EC level M with mask 0, BCH(15,5) encoded, then XORed with
 * the spec's fixed pattern. Precomputed — it is one constant, and deriving it at
 * runtime would be arithmetic in service of nothing.
 */
const FORMAT_M_MASK0 = 0b101010000010010;

function placeFormat(modules: Int8Array, size: number) {
  // The grid is [row * size + column]. The first copy runs DOWN column 8 and
  // then LEFT along row 8; an earlier version had those two axes swapped, which
  // produced a symbol that looked entirely correct and decoded as nothing —
  // format bits are the first thing a scanner reads, and a wrong EC level makes
  // the rest unreadable however clean it is.
  for (let i = 0; i < 15; i++) {
    const on = ((FORMAT_M_MASK0 >> i) & 1) === 1 ? 1 : 0;

    // First copy, around the top-left finder.
    if (i < 6) modules[i * size + 8] = on;
    else if (i === 6) modules[7 * size + 8] = on;
    else if (i === 7) modules[8 * size + 8] = on;
    else if (i === 8) modules[8 * size + 7] = on;
    else modules[8 * size + (14 - i)] = on;

    // Second copy: the low eight bits run LEFT along row 8 from the top-right
    // corner, the high seven run DOWN column 8 to the bottom-left. Swapping
    // these two halves still decodes — the first copy is intact and the format
    // is BCH-protected — so the symbol scans fine on a clean screen and starts
    // failing on a scuffed card, when that redundancy is all that is left.
    if (i < 8) modules[8 * size + (size - 1 - i)] = on;
    else modules[(size - 15 + i) * size + 8] = on;
  }
}
